import React, { useEffect, useMemo, useState } from 'react';

import { Button, DatePicker, Input, Modal, Select, Space, Typography, message } from 'antd';

import dayjs from 'dayjs';

import { useSearchParams } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import {

  analyticsDateRangeForPreset,

  defaultAnalyticsDateRange,

  getSalesAnalyticsFilterOptions,

  parseAnalyticsFiltersFromSearchParams,

  type AnalyticsPeriodPreset,

  type SalesAnalyticsFilterParams,

} from '@/services/salesAnalytics.service';

import { listAssignableSalesTeams } from '@/services/salesTeam.service';

import api from '@/services/api.service';

import { useAuthStore } from '@/store/authStore';

import {

  createSavedView,

  persistSavedViews,

  readSavedViewsFromUser,

  type SalesAnalyticsSavedView,

} from './analyticsSavedViews';



const { RangePicker } = DatePicker;

const { Text } = Typography;



const PERIOD_PRESETS: { key: AnalyticsPeriodPreset; label: string }[] = [

  { key: 'mtd', label: 'MTD' },

  { key: 'qtd', label: 'QTD' },

  { key: 'ytd', label: 'YTD' },

  { key: 'last30', label: 'Last 30' },

  { key: 'last90', label: 'Last 90' },

];



function parseUserPrefs(user: ReturnType<typeof useAuthStore.getState>['user']): Record<string, unknown> {

  if (!user?.preferences) return {};

  try {

    return typeof user.preferences === 'string'

      ? (JSON.parse(user.preferences) as Record<string, unknown>)

      : (user.preferences as Record<string, unknown>);

  } catch {

    return {};

  }

}



export function useSalesAnalyticsFilters(): {

  filters: SalesAnalyticsFilterParams;

  setFilter: (key: keyof SalesAnalyticsFilterParams, value?: string) => void;

  setDateRange: (from?: string, to?: string) => void;

  applyPreset: (preset: AnalyticsPeriodPreset) => void;

  clearAllFilters: () => void;

  applySavedView: (view: SalesAnalyticsSavedView) => void;

  filterSummary: string;

} {

  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => parseAnalyticsFiltersFromSearchParams(searchParams), [searchParams]);



  const teamsQuery = useQuery({

    queryKey: ['sales-teams-assignable'],

    queryFn: listAssignableSalesTeams,

  });

  const customersQuery = useQuery({

    queryKey: ['sales-customers-list'],

    queryFn: async () => {

      const res = await api.get('/sales/customers/list');

      const data = (res.data as { data?: unknown[] })?.data ?? res.data;

      return Array.isArray(data) ? data : [];

    },

  });



  const setFilter = (key: keyof SalesAnalyticsFilterParams, value?: string) => {

    const next = new URLSearchParams(searchParams);

    if (value) next.set(key, value);

    else next.delete(key);

    setSearchParams(next, { replace: true });

  };



  const setDateRange = (from?: string, to?: string) => {

    const next = new URLSearchParams(searchParams);

    const defaults = defaultAnalyticsDateRange();

    next.set('date_from', from ?? defaults.date_from);

    next.set('date_to', to ?? defaults.date_to);

    setSearchParams(next, { replace: true });

  };



  const applyPreset = (preset: AnalyticsPeriodPreset) => {

    const { date_from, date_to } = analyticsDateRangeForPreset(preset);

    setDateRange(date_from, date_to);

  };



  const clearAllFilters = () => {

    const defaults = defaultAnalyticsDateRange();

    const next = new URLSearchParams(searchParams);

    next.set('date_from', defaults.date_from);

    next.set('date_to', defaults.date_to);

    ['sales_team_id', 'customer_id', 'revenue_service_type_id', 'industry', 'country', 'compare_to'].forEach(

      (k) => next.delete(k)

    );

    next.set('funnel_mode', 'volume');

    setSearchParams(next, { replace: true });

  };



  const applySavedView = (view: SalesAnalyticsSavedView) => {

    const next = new URLSearchParams();

    const f = view.filters;

    if (f.date_from) next.set('date_from', f.date_from);

    if (f.date_to) next.set('date_to', f.date_to);

    if (f.sales_team_id) next.set('sales_team_id', f.sales_team_id);

    if (f.customer_id) next.set('customer_id', f.customer_id);

    if (f.revenue_service_type_id) next.set('revenue_service_type_id', f.revenue_service_type_id);

    if (f.industry) next.set('industry', f.industry);

    if (f.country) next.set('country', f.country);

    if (f.funnel_mode) next.set('funnel_mode', f.funnel_mode);

    if (f.compare_to) next.set('compare_to', f.compare_to);

    if (searchParams.get('tab')) next.set('tab', searchParams.get('tab')!);

    if (searchParams.get('analytics_tab')) next.set('analytics_tab', searchParams.get('analytics_tab')!);

    setSearchParams(next, { replace: true });

  };



  const teamName = (teamsQuery.data ?? []).find(

    (t: { id: string }) => t.id === filters.sales_team_id

  ) as { team_name?: string } | undefined;

  const customerName = (customersQuery.data ?? []).find(

    (c: { id: string }) => c.id === filters.customer_id

  ) as { customer_name?: string } | undefined;



  const filterSummary = [

    `${filters.date_from} → ${filters.date_to}`,

    teamName?.team_name ? `Team: ${teamName.team_name}` : filters.sales_team_id ? 'Team filtered' : null,

    customerName?.customer_name

      ? `Customer: ${customerName.customer_name}`

      : filters.customer_id

        ? 'Customer filtered'

        : null,

    filters.country ? `Country: ${filters.country}` : null,

    filters.industry ? `Industry: ${filters.industry}` : null,

    filters.funnel_mode === 'cohort' ? 'Cohort funnel' : null,

    filters.compare_to ? `Compare: ${filters.compare_to.replace('_', ' ')}` : null,

  ]

    .filter(Boolean)

    .join(' · ');



  return { filters, setFilter, setDateRange, applyPreset, clearAllFilters, applySavedView, filterSummary };

}



export const SalesAnalyticsFilterBar: React.FC = () => {

  const { filters, setFilter, setDateRange, applyPreset, clearAllFilters, applySavedView } =

    useSalesAnalyticsFilters();

  const user = useAuthStore((s) => s.user);

  const setUser = useAuthStore((s) => s.setUser);

  const [countryDraft, setCountryDraft] = useState(filters.country ?? '');

  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const [viewName, setViewName] = useState('');

  const [savedViews, setSavedViews] = useState<SalesAnalyticsSavedView[]>(() => readSavedViewsFromUser(user));



  useEffect(() => {

    setSavedViews(readSavedViewsFromUser(user));

  }, [user]);



  useEffect(() => {

    setCountryDraft(filters.country ?? '');

  }, [filters.country]);



  useEffect(() => {

    const t = window.setTimeout(() => {

      const trimmed = countryDraft.trim();

      if ((filters.country ?? '') !== trimmed) {

        setFilter('country', trimmed || undefined);

      }

    }, 300);

    return () => window.clearTimeout(t);

  }, [countryDraft, filters.country, setFilter]);



  const teamsQuery = useQuery({

    queryKey: ['sales-teams-assignable'],

    queryFn: listAssignableSalesTeams,

  });



  const filterOptionsQuery = useQuery({

    queryKey: ['sales-analytics-filter-options'],

    queryFn: getSalesAnalyticsFilterOptions,

  });



  const rstQuery = useQuery({

    queryKey: ['sales-revenue-service-types', 'active'],

    queryFn: async () => {

      const res = await api.get('/sales/revenue-service-types?only_active=true');

      const data = (res.data as { data?: unknown[] })?.data ?? res.data;

      return Array.isArray(data) ? data : [];

    },

  });



  const customersQuery = useQuery({

    queryKey: ['sales-customers-list'],

    queryFn: async () => {

      const res = await api.get('/sales/customers/list');

      const data = (res.data as { data?: unknown[] })?.data ?? res.data;

      return Array.isArray(data) ? data : [];

    },

  });



  const handleSaveView = async () => {

    if (!user?.id || !viewName.trim()) return;

    const view = createSavedView(viewName, filters);

    const nextViews = [...savedViews, view];

    try {

      const prefs = parseUserPrefs(user);

      await persistSavedViews(user.id, prefs, nextViews);

      setSavedViews(nextViews);

      if (user) {

        setUser({ ...user, preferences: JSON.stringify({ ...prefs, sales_analytics_saved_views: nextViews }) });

      }

      message.success('View saved');

      setSaveModalOpen(false);

      setViewName('');

    } catch (e) {

      message.error(e instanceof Error ? e.message : 'Failed to save view');

    }

  };



  return (

    <>

      <Space wrap size="middle" style={{ width: '100%' }}>

        <Space direction="vertical" size={0}>

          <Text type="secondary" style={{ fontSize: 11 }}>

            Period

          </Text>

          <Space wrap size={4}>

            {PERIOD_PRESETS.map((p) => (

              <Button key={p.key} size="small" onClick={() => applyPreset(p.key)}>

                {p.label}

              </Button>

            ))}

          </Space>

          <RangePicker

            style={{ marginTop: 4 }}

            value={[dayjs(filters.date_from), dayjs(filters.date_to)]}

            onChange={(range) => {

              if (!range?.[0] || !range[1]) return;

              setDateRange(range[0].format('YYYY-MM-DD'), range[1].format('YYYY-MM-DD'));

            }}

            allowClear={false}

          />

        </Space>

        <Space direction="vertical" size={0}>

          <Text type="secondary" style={{ fontSize: 11 }}>

            Sales team

          </Text>

          <Select

            allowClear

            placeholder="All teams"

            style={{ minWidth: 160 }}

            value={filters.sales_team_id}

            onChange={(v) => setFilter('sales_team_id', v)}

            options={(teamsQuery.data ?? []).map((t: { id: string; team_name: string }) => ({

              label: t.team_name,

              value: t.id,

            }))}

          />

        </Space>

        <Space direction="vertical" size={0}>

          <Text type="secondary" style={{ fontSize: 11 }}>

            Customer

          </Text>

          <Select

            allowClear

            showSearch

            placeholder="All customers"

            style={{ minWidth: 180 }}

            optionFilterProp="label"

            value={filters.customer_id}

            onChange={(v) => setFilter('customer_id', v)}

            options={(customersQuery.data ?? []).map((c: { id: string; customer_name: string }) => ({

              label: c.customer_name,

              value: c.id,

            }))}

          />

        </Space>

        <Space direction="vertical" size={0}>

          <Text type="secondary" style={{ fontSize: 11 }}>

            Revenue service type

          </Text>

          <Select

            allowClear

            placeholder="All RST"

            style={{ minWidth: 140 }}

            value={filters.revenue_service_type_id}

            onChange={(v) => setFilter('revenue_service_type_id', v)}

            options={(rstQuery.data ?? []).map((r: { id: string; service_code: string; service_name: string }) => ({

              label: `${r.service_code} — ${r.service_name}`,

              value: r.id,

            }))}

          />

        </Space>

        <Space direction="vertical" size={0}>

          <Text type="secondary" style={{ fontSize: 11 }}>

            Industry

          </Text>

          <Select

            allowClear

            placeholder="All industries"

            style={{ minWidth: 140 }}

            value={filters.industry}

            onChange={(v) => setFilter('industry', v)}

            options={(filterOptionsQuery.data?.industries ?? []).map((v) => ({ label: v, value: v }))}

          />

        </Space>

        <Space direction="vertical" size={0}>

          <Text type="secondary" style={{ fontSize: 11 }}>

            Country

          </Text>

          <Select

            allowClear

            showSearch

            placeholder="Country"

            style={{ minWidth: 140 }}

            value={filters.country || undefined}

            onSearch={setCountryDraft}

            onChange={(v) => {

              setCountryDraft(v ?? '');

              setFilter('country', v);

            }}

            options={(filterOptionsQuery.data?.countries ?? []).map((v) => ({ label: v, value: v }))}

            dropdownRender={(menu) => (

              <>

                {menu}

                <div style={{ padding: 8 }}>

                  <Input

                    placeholder="Type country…"

                    value={countryDraft}

                    onChange={(e) => setCountryDraft(e.target.value)}

                    allowClear

                  />

                </div>

              </>

            )}

          />

        </Space>

        <Space direction="vertical" size={0}>

          <Text type="secondary" style={{ fontSize: 11 }}>

            Funnel

          </Text>

          <Select

            style={{ minWidth: 120 }}

            value={filters.funnel_mode ?? 'volume'}

            onChange={(v) => setFilter('funnel_mode', v)}

            options={[

              { label: 'Volume', value: 'volume' },

              { label: 'Cohort', value: 'cohort' },

            ]}

          />

        </Space>

        <Space direction="vertical" size={0}>

          <Text type="secondary" style={{ fontSize: 11 }}>

            Compare

          </Text>

          <Select

            allowClear

            placeholder="None"

            style={{ minWidth: 130 }}

            value={filters.compare_to}

            onChange={(v) => setFilter('compare_to', v)}

            options={[

              { label: 'Prior period', value: 'prior_period' },

              { label: 'Prior year', value: 'prior_year' },

            ]}

          />

        </Space>

        <Space direction="vertical" size={0}>

          <Text type="secondary" style={{ fontSize: 11 }}>

            Views

          </Text>

          <Space wrap>

            <Select

              allowClear

              placeholder="My views"

              style={{ minWidth: 140 }}

              onChange={(id) => {

                const view = savedViews.find((v) => v.id === id);

                if (view) applySavedView(view);

              }}

              options={savedViews.map((v) => ({ label: v.name, value: v.id }))}

            />

            <Button size="small" onClick={() => setSaveModalOpen(true)}>

              Save view

            </Button>

            <Button size="small" onClick={clearAllFilters}>

              Clear all

            </Button>

          </Space>

        </Space>

      </Space>



      <Modal

        title="Save analytics view"

        open={saveModalOpen}

        onOk={() => void handleSaveView()}

        onCancel={() => setSaveModalOpen(false)}

        okText="Save"

      >

        <Input

          placeholder="View name"

          value={viewName}

          onChange={(e) => setViewName(e.target.value)}

          onPressEnter={() => void handleSaveView()}

        />

      </Modal>

    </>

  );

};


